import * as React from 'react';
import {SocialAuthIntegration} from '@practicegenius/domain/src/data/integrations';
import {openPopup as sharedOpenPopup} from '@practicegenius/pg-ui/src/utils/openPopup';
import {
  AppToSitesMessage,
  makeChannelUpdateMessage,
} from '@practicegenius/domain/src/sites-mobile-app/messages';
import {addQueryParams} from '@practicegenius/pg-util/src/UrlUtils';
import {Logger} from '@practicegenius/domain/src/common/logger';
import Echo from 'laravel-echo';
import {inMobileApp, postMessageToMobileApp} from '../utils/MobileApp';
import {browserInfo} from '../utils/browser';
import {LastUsedSocialProviderManager} from '../utils/lastUsedSocialProvider';
import {getSocketId} from '../utils/websocket';

type AlreadyRegisteredLinks = {
  register: boolean;
  forgot_password: boolean;
};

type AlreadyRegisteredHint = {
  email: string;
  links: AlreadyRegisteredLinks;
};
type AlreadySocialRegisteredHint = AlreadyRegisteredHint & {
  providers: SocialAuthIntegration[];
};

type SocialOauthError =
  | {
      code: 'account_registered';
      message: string;
      hint: AlreadyRegisteredHint;
    }
  | {
      code: 'social_registered';
      message: string;
      hint: AlreadySocialRegisteredHint;
    }
  | {
      code: 'generic';
      errors: Record<string, string>;
    };

export type SocialOauthAppSpecificProps = {
  getWebsocket: () => Echo;
  lastUsedSocialProviderManager: LastUsedSocialProviderManager;
  logger: Logger;
  coreServiceUrl: string;
};

export type UseSocialOauthProps = {
  appProps: SocialOauthAppSpecificProps;
  /** Enable the social oauth websocket & popup logic */
  enabled?: boolean;
  platform: SocialAuthIntegration;
  authPopupUrl: string;
  onAuthSuccess: (a: any) => unknown;
  onAuthError?: (a: SocialOauthError) => unknown;
};

export const useSocialOauth = ({
  appProps,
  enabled = true,
  platform,
  authPopupUrl,
  onAuthSuccess,
  onAuthError,
}: UseSocialOauthProps) => {
  // In the mobile app, buttons do not start disabled because we auth via the native app
  const [authDisabled, setAuthDisabled] = React.useState(!inMobileApp);
  const [socketId, setSocketId] = React.useState<string | null | undefined>(
    null,
  );
  const popupRef = React.useRef<Window | null>(null);
  const _unmounted = React.useRef(false);
  const isUnmounted = _unmounted.current === true;

  const closePopup = () => popupRef.current?.close();

  const openPopup = (): Window | null => {
    if (!socketId) {
      return null;
    }

    const popup = sharedOpenPopup(
      addQueryParams(authPopupUrl, {socket_id: socketId}),
    );

    return popup;
  };

  /** Periodically checks the popup window's status. When it closes, auth is re-enabled */
  const setupPopupChecker = () => {
    const check = setInterval(() => {
      const popup = popupRef.current;

      if (!popup || popup.closed || popup.closed === undefined) {
        clearInterval(check);

        if (!isUnmounted) {
          setAuthDisabled(false);
        }
      }
    }, 1000);
  };

  const startAuth = () => {
    if (enabled && !authDisabled) {
      if (inMobileApp) {
        const openAuthUrl = (url: string) => {
          postMessageToMobileApp({
            type: 'request_open_external_url',
            payload: {
              url,
            },
          });
        };

        const triggerSdkAuth = () => {
          postMessageToMobileApp({
            type: 'request_social_auth',
            payload: {
              platform,
            },
          });
        };

        /** if android and facebook, temporarily use the web flow to authenticate the user while the mobile app is being updated with the latest SDK
         * @see https://practicegenius.atlassian.net/browse/PAT-15695
         */
        if (browserInfo?.getOSName() === 'Android' && platform === 'facebook') {
          // Super-mega edge-case: for registration we build an api-core URL, but this url expects to redirect to the provider.
          // Instead, we want and endpoint from which we can fetch the URL.
          // That endpoint happends to be derivable from authPopupUrl, so we'll do some hacky shit for now to make this work.
          let fetchUrl = authPopupUrl;
          if (authPopupUrl.startsWith(appProps.coreServiceUrl)) {
            // split query string, add /url, and rejoin
            const [url, query] = authPopupUrl.split('?');
            fetchUrl = `${url}/url?${query}`;
          }
          // Otherwise, we have a local path so we need to fetch the full URL
          return (
            fetch(fetchUrl, {
              headers: {
                Accept: 'application/json',
              },
            })
              .then(res => res.json())
              // This opens the provider's oauth URL in the device's browser. When successful, that will redirect back to api-core which should redirect back to the app :finger_crossed:
              .then(data => {
                // local endpoints return authUrl
                if ('authUrl' in data) {
                  return openAuthUrl(data.authUrl);
                }

                // api-core endpoints return oauth_url
                if ('oauth_url' in data) {
                  return openAuthUrl(data.oauth_url);
                }

                throw new Error('auth url not found in response');
              })
              .catch(error => {
                // If we can't get the URL, then we'll trigger SDK auth as a fallback
                appProps.logger.error('Failed to fetch authPopupUrl', error);
                triggerSdkAuth();
              })
          );
        }

        triggerSdkAuth();
      } else {
        popupRef.current = openPopup();
        setupPopupChecker();
        setAuthDisabled(true);
      }
    }
  };

  const subscribeToWebsocket = async () => {
    const websocket = appProps.getWebsocket();
    const getSocketIdResult = await getSocketId(websocket);

    if (getSocketIdResult.status === 'error') {
      throw new Error(
        `Could not subscribe to websocket channel for socialauth: ${getSocketIdResult.errorType}`,
      );
    }

    if (isUnmounted) {
      return;
    }

    const newSocketId = getSocketIdResult.socketId;

    setAuthDisabled(false);
    setSocketId(newSocketId);

    websocket
      .channel(`socialauth.${newSocketId}`)
      .listen(`.${platform}`, (payload: any) => {
        closePopup();
        appProps.lastUsedSocialProviderManager.set(platform);
        onAuthSuccess(payload);
      })
      .listen(`.${platform}.error`, (payload: any) => {
        closePopup();

        if (onAuthError) {
          onAuthError(payload);
        }
      });
  };

  const unsubscribeFromWebsocket = () => {
    const websocket = appProps.getWebsocket();

    if (typeof socketId === 'string') {
      websocket.leaveChannel(`socialauth.${socketId}`);
    }
  };

  // On Mount/Unmount
  React.useEffect(() => {
    // If the hook is not enabled, then do not do not set anything up
    if (!enabled) {
      return;
    }

    if (inMobileApp) {
      const handlePostMessage = (event: MessageEvent) => {
        if (typeof event.data !== 'string') return;

        try {
          const data = JSON.parse(event.data) as AppToSitesMessage;
          if (
            data.channel === 'socialAuth' &&
            data.type === 'receive_social_auth_success' &&
            data.payload.platform === platform
          ) {
            appProps.lastUsedSocialProviderManager.set(platform);
            onAuthSuccess({
              providerToken: data.payload.token,
              extraData: data.payload.extraData,
            });
          }
        } catch (e) {
          // This might handle messages from other sources and they might not be JSON, so we need to ignore them
          if (e instanceof SyntaxError) {
            // JSON failed to parse, so it must not be a known message
            return;
          }

          // otherwise, the error is unexpected and should be thrown
          throw e;
        }
      };

      window.addEventListener('message', handlePostMessage, true);
      postMessageToMobileApp(makeChannelUpdateMessage('socialAuth', true));
      return () => {
        _unmounted.current = true;
        window.removeEventListener('message', handlePostMessage, true);
        postMessageToMobileApp(makeChannelUpdateMessage('socialAuth', false));
      };
    }

    const handlePopupPostMessage = ({
      data,
    }: {
      data: {
        action: string;
        payload: any;
      };
    }) => {
      if (data && data.action === 'social_auth_error') {
        if (onAuthError) {
          onAuthError({
            code: 'generic',
            errors: data.payload.errors,
          });
        }

        closePopup();
      }
    };

    if (!socketId) subscribeToWebsocket();

    window.addEventListener('message', handlePopupPostMessage, false);

    return () => {
      _unmounted.current = true;
      window.removeEventListener('message', handlePopupPostMessage);
      unsubscribeFromWebsocket();
    };
  }, [enabled]);

  return {startAuth, disabled: authDisabled};
};
